import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { countryAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const CountryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadCountry = useCallback(async () => {
    try {
      setLoading(true);
      const response = await countryAPI.getById(id);
      setCountry(response.data);
    } catch (err) {
      setErrorMessage(t('country.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadCountry();
  }, [loadCountry]);

  const headerActions = (
    <div className="ad-toolbar-group">
      <button type="button" className="ad-btn" onClick={() => navigate('/country')}>
        <i className="fas fa-arrow-right"></i>
        {t('country.view.backToList')}
      </button>
      <button
        type="button"
        className="ad-btn ad-btn-primary"
        onClick={() => navigate(`/country/edit/${id}`)}
      >
        <i className="fas fa-pen"></i>
        {t('country.view.edit')}
      </button>
    </div>
  );

  if (loading) {
    return (
      <AdminPage title={t('country.view.title')}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  if (!country) {
    return (
      <AdminPage title={t('country.view.title')}>
        <StateBlock icon="fa-circle-exclamation" title={t('country.list.messages.noData')} />
        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />
      </AdminPage>
    );
  }

  const items = [
    { label: t('country.view.fields.countryCode'), value: country.countryCode },
    { label: t('country.view.fields.nameAr'), value: country.nameAr },
    { label: t('country.view.fields.nameEng'), value: country.nameEng },
    { label: t('country.view.fields.currency'), value: country.currencyId },
    { label: t('country.view.fields.timeZone'), value: country.timeZone },
    { label: t('country.view.fields.idNationalLength'), value: country.idNationalLength },
    { label: t('country.view.fields.mobileNumberLength'), value: country.mobileNumberLength },
  ];

  return (
    <AdminPage
      title={country.nameAr || t('country.view.title')}
      subtitle={country.nameEng}
      headerActions={headerActions}
    >
      <div className="ad-detail">
        <div className="ad-detail-grid">
          {items.map((item) => (
            <div key={item.label} className="ad-detail-item">
              <span className="ad-detail-label">{item.label}</span>
              <span className="ad-detail-value">
                {item.value === null || item.value === undefined || item.value === ''
                  ? '-'
                  : item.value}
              </span>
            </div>
          ))}

          <div className="ad-detail-item">
            <span className="ad-detail-label">{t('country.view.fields.isActive')}</span>
            <span>
              <span
                className={`ad-badge ${country.isActive ? 'ad-badge-success' : 'ad-badge-danger'}`}
              >
                {country.isActive ? t('common.active') : t('common.inactive')}
              </span>
            </span>
          </div>

          <div className="ad-detail-item">
            <span className="ad-detail-label">
              {t('country.view.fields.isArabicCountry')}
            </span>
            <span>
              <span className="ad-badge">
                {country.isArabicCountry ? t('common.yes') : t('common.no')}
              </span>
            </span>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default CountryView;
